import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  CategoryResultCont,
  GroupInfoCont,
  KpiCont,
  LinkCont,
  ReportLink,
  ResponseCount,
  ResponseRate,
  ResponseRateCont,
  Risk,
  Score,
  TeamName,
  TileCont,
} from './GroupComparisonTableTile.styles';
import { RouteNames } from '../../../constans/routes';
import { useGroupComparison } from '../../../hooks';
import { ITeamComparisonItem } from '../../../services/groupComparison';
import { CategoriesResultsCont } from '../GroupComparisonTable.styles';
import ReportIcon from '@/assets/icons/report.svg?react';

interface IGroupComparisonTableTile {
  isRiskAvailable: boolean;
  isKpiAvailable: boolean;
  item: ITeamComparisonItem;
}

const GroupComparisonTableTile: FC<IGroupComparisonTableTile> = ({
  isRiskAvailable,
  isKpiAvailable,
  item,
}) => {
  const { t } = useTranslation();
  const { getRatingColor, getRepresentativenessColor, getRiskColor } = useGroupComparison();
  const riskLevel = item.risk?.level || 'potential';
  const colors = useMemo(() => {
    const repColor = getRepresentativenessColor(item.responsiveness.representativeness);
    const riskColor = getRiskColor(item.risk?.level);
    const kpiColor = getRatingColor(item.kpi?.rating);
    return {
      representativeness: repColor,
      kpi: kpiColor,
      risk: riskColor,
    };
  }, [
    getRatingColor,
    getRepresentativenessColor,
    getRiskColor,
    item.kpi?.rating,
    item.responsiveness.representativeness,
    item.risk?.level,
  ]);

  const isTeamReportAvailable = useMemo(() => _.has(item, 'reportId'), [item]);

  const renderCategoriesScore = useMemo(() => {
    return item.workEnvironment.map(element => {
      const scoreColor = getRatingColor(element.result.rating);
      return (
        <CategoryResultCont id="CategoryResultCont" key={element.category.code} color={scoreColor}>
          <Score id="Score">{element.result.score}</Score>
        </CategoryResultCont>
      );
    });
  }, [getRatingColor, item.workEnvironment]);

  return (
    <TileCont id="TileCont" data-testid="groupComparisonTile">
      <GroupInfoCont id="GroupInfoCont">
        <TeamName id="TeamName" isKpiAvailable={isKpiAvailable} isRiskAvailable={isRiskAvailable}>
          {item.team.name}
        </TeamName>
        <ResponseRateCont id="ResponseRateCont">
          <ResponseRate id="ResponseRate" color={colors.representativeness}>
            {item.responsiveness.responseRate}%
          </ResponseRate>
          <ResponseCount id="ResponseCount">
            ({item.responsiveness.responseCount}/{item.responsiveness.participantCount})
          </ResponseCount>
        </ResponseRateCont>
        {isRiskAvailable && (
          <Risk id="Risk" color={colors.risk}>
            {t(`report.groupComparison.riskLevel.${riskLevel}`)}
          </Risk>
        )}
        {isKpiAvailable && (
          <KpiCont id="KpiCont" color={colors.kpi}>
            <Score id="Score">{item.kpi?.score}</Score>
          </KpiCont>
        )}
      </GroupInfoCont>
      <CategoriesResultsCont id="CategoriesResultsCont">
        {renderCategoriesScore}
      </CategoriesResultsCont>
      <ReportLink id="ReportLink" isTeamReportAvailable={isTeamReportAvailable}>
        {isTeamReportAvailable && (
          <LinkCont to={`${RouteNames.Reports}/${item.reportId}`} target="_blank">
            <ReportIcon />
          </LinkCont>
        )}
      </ReportLink>
    </TileCont>
  );
};

export default GroupComparisonTableTile;
